import { Link } from "react-router-dom";
import { Activity, Building2, Wallet, Smile, ShieldCheck, LineChart, BedDouble, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO = "https://images.pexels.com/photos/39192383/pexels-photo-39192383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FEATURES = [
  [Building2, "Multi-clinic groups", "Open one or many clinics, each with its own country, currency and manager."],
  [Wallet, "Accountant-led payments", "Every shilling, dollar or rand is recorded by the accountant — cash, M-Pesa, card or insurance."],
  [Smile, "Procedures, dental & insurance", "Switch on the units each clinic runs and bill them on one invoice."],
  [LineChart, "Executive dashboard", "Income, expenses, cash flow, HR and consultations for confident decisions."],
  [ShieldCheck, "Role-based access", "CEO → Manager → departmental staff. Everyone sees only what they need."],
  [BedDouble, "Inpatient, next", "Wards, beds and admissions are on the roadmap as an add-on module."],
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#f6f8f7]">
      <header className="mx-auto flex max-w-7xl items-center justify-between px-6 py-6">
        <div className="flex items-center gap-2">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-teal-700 text-white"><Activity className="h-4 w-4" /></span>
          <span className="font-display text-xl font-semibold">Klinic</span>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" asChild className="rounded-full" data-testid="landing-login-link"><Link to="/login">Sign in</Link></Button>
          <Button asChild className="rounded-full" data-testid="landing-register-link"><Link to="/register">Register as CEO</Link></Button>
        </div>
      </header>
      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 pb-20 pt-8 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-6 rise">
          <p className="mb-5 inline-flex rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">Outpatient clinic operating system</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight text-slate-900">
            Run every clinic in your group from <span className="text-teal-700">one calm command centre.</span>
          </h1>
          <p className="mt-6 max-w-xl text-base text-slate-600">Create clinics, appoint managers, register patients, generate invoices and let your accountant record every payment — while you watch income, expenses and cash flow live.</p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild size="lg" className="rounded-full px-7 active:scale-[0.98]" data-testid="hero-get-started-button"><Link to="/register">Start your clinic group <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
            <Button asChild size="lg" variant="outline" className="rounded-full px-7" data-testid="hero-login-button"><Link to="/login">Staff sign in</Link></Button>
          </div>
        </div>
        <div className="relative lg:col-span-6 rise" style={{ animationDelay: "120ms" }}>
          <img src={HERO} alt="Clinic reception" className="h-[420px] w-full rounded-[2rem] object-cover shadow-xl" />
          <div className="absolute -bottom-6 left-6 rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-lg backdrop-blur-md">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Net cash flow</p>
            <p className="font-display text-2xl font-semibold text-emerald-700">Live, per clinic</p>
          </div>
        </div>
      </section>
      <section className="bg-slate-950 py-20 text-slate-100">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="max-w-xl text-2xl sm:text-3xl font-semibold tracking-tight">Built around how clinic groups actually work.</h2>
          <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(([Icon, t, d]) => (
              <div key={t} className="bg-slate-950 p-8 transition-colors hover:bg-slate-900">
                <Icon className="h-5 w-5 text-teal-400" />
                <h3 className="mt-5 text-lg font-semibold">{t}</h3>
                <p className="mt-2 text-sm text-slate-400">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <footer className="px-6 py-8 text-center text-xs text-slate-400">© {new Date().getFullYear()} Klinic</footer>
    </div>
  );
}
